import styles from '../../../styles/screening.module.css';
import { Col, Row, Table, Button, Spinner } from "react-bootstrap";
import { useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useGetScreening, useScreeningUpsert } from '../../../new/query';
import { useEffect, useMemo } from 'react';
import { PrepareNullableDataForSend } from '../../../utilities/rhfdatanormalizationfunctions';
import useMessageStore from '../../../stores/message-store';


const ScreeningForm = () => {
    const {ApplicationID} = useOutletContext();

    const {isLoading,data} = useGetScreening(ApplicationID);

    const initialValues = useMemo(() => {
        return {
            ApplicationID: `${ApplicationID}`,
            screeningCompletedDate: '',
            ambulationLevel: "",
            transferLevel: "",
            toiletingLevel: "",
            hygieneLevel: "",
            cognitionLevel: "",
            medicalLevel: "",
            communicationLevel: "",
            comments: "" 
        }
    },[ApplicationID])

    const ambulationData = [
        { title: 'Independent', value: '1' },
        { title: 'Requires assistance via use of a device; or requires assistance of 1 person', value: '2' },
        { title: 'Requires assistance via use of a device and 1 or more person(s); or requires assistance of 2 or more persons', value: '3' }
    ];

    const trasnferHygeineData = [
        { title: 'Independent', value: '1' },
        { title: 'Requires assistance of 1 person', value: '2' },
        { title: 'Requires assistance of 2 or more persons', value: '3' }
    ];

    const toiletingData = [
        { title: 'Independent or Occasionally incontinent but requires no assistance', value: '1' },
        { title: 'Requires assistance of 1 person', value: '2' },
        { title: 'Requires assistance of 2 or more persons', value: '3' }
    ];

    const cognitionData = [
        { title: 'No problems', value: '1' },
        { title: 'Decisions poor (e.g., cues, supervision required, behavior indicators present but easily altered)', value: '2' },
        { title: 'Severely impaired (e.g., never or rarely makes decisions/behavior indicators present and not easily altered)', value: '3' }
    ];

    const medicalNursingData = [
        { title: 'Stable with routine medical/nursing monitoring and care', value: '1' },
        { title: 'Requires skilled treatment or frequent monitoring in addition to routine medication administration, (e.g., sterile dressings, new colostomy, etc.)', value: '2' },
        { title: 'Requires intense professional intervention to provide skilled treatment (e.g., tracheotomy, IV access, NG/G Tube care, extensive decub ulcers, etc.)', value: '3' }
    ];

    const communication = [
        { title: 'No problem, able to understand and be understood. Able to make needs known. May require communication tools, board', value: '1'},
        { title: 'Usually understood. May have difficulty (e.g., finding words), responds best to simple, direct communication', value: '2' },
        { title: 'Rarely/Never able to understand or to make self understood. Unable to make needs known.', value: '3' }
    ];

    const {register,reset,handleSubmit,watch, formState: {isDirty}} = useForm({defaultValues: initialValues});

    const formValues = watch(['ambulationLevel','transferLevel','toiletingLevel','hygieneLevel','cognitionLevel','medicalLevel','communicationLevel']);

    const { level1Total, level2Total, level3Total } = Object.values(formValues).reduce(
        (acc, val) => { 
            if (val === '1') acc.level1Total += 1;
            if (val === '2') acc.level2Total += 1;
            if (val === '3') acc.level3Total += 1;
            return acc;
        },
        { level1Total: 0, level2Total: 0, level3Total: 0 }
    );

    const totalScore = level1Total + level2Total + level3Total;

    const acuityDeterminationLevel = [level1Total,level2Total,level3Total].indexOf(Math.max(level1Total,level2Total,level3Total)) + 1;

    const addToast = useMessageStore(state => state.addToast);

    const successCallback = () => {
        addToast(
            {
                id: Math.random(),
                body: "The Screening was succesfully updated!",
                autoHide: true,
                errorFlag: false,
                timestamp: Date.now() - 1000
            }
        )
    }

    const errorCallback = () => {
        addToast(
            {
                id: Math.random(),
                body: "An error occured when updating the Screening.",
                autoHide: true,
                errorFlag: true,
                timestamp: Date.now() - 1000
            }
        )
    }

    const mutation = useScreeningUpsert(ApplicationID,successCallback,errorCallback);

    const _submitFn = (data) => {
        const payload = PrepareNullableDataForSend(data);
        mutation.mutate(payload);
    }

    useEffect(() => {
        if(!data){
            //Reset to default values
            reset(initialValues);
        }
        else{
            //Reset to returned values
            const extractedData = Object.fromEntries(
                Object.entries(data).filter(([key]) => key !== 'applicationID')
            );

            const levelKeys = ['ambulationLevel','transferLevel','toiletingLevel','hygieneLevel','cognitionLevel','medicalLevel','communicationLevel'];

            const resetObj = {
                ...extractedData,
                ...Object.fromEntries(
                    levelKeys.map((key) => [key,extractedData[key] === null ? '' : String(extractedData[key])])
                )
            }
            resetObj.ApplicationID = ApplicationID;

            reset(resetObj);
        }
    },[data,reset,initialValues,ApplicationID])

    if(isLoading){
        return(
            <div>loading...</div>
        )
    }

    return(
        <div className={`${styles.screeningContainer} clientCard`}>
            <h4 className='text-center'>Screening</h4>
            <form onSubmit={handleSubmit(_submitFn)}>
                <input type='hidden' {...register('ApplicationID')} />
                <Row>
                    <Col xs={12} md={4}>
                        <label htmlFor='screeningCompletedDate'>Screening Completed Date</label>
                        <input type='date' id='screeningCompletedDate' {...register('screeningCompletedDate')} style={{width: '100%'}} disabled={mutation.isPending}/>
                    </Col>
                </Row>
                <Row className='mt-4'>
                    <Col xs={12}>
                        <strong>
                            A score of level 3 on any area requires discussion on viability of transition with STT staff. Gather all necessary documentation for support of transition.
                        </strong>
                    </Col>
                    <Col xs={12}>
                    <div className=''> 
                        <Table striped bordered hover responsive className={styles.responsiveTable}>
                            <thead>
                                <tr>
                                    <td></td>
                                    <td className="desktopOnly"><strong>Level 1</strong></td>
                                    <td className="desktopOnly"><strong>Level 2</strong></td>
                                    <td className="desktopOnly"><strong>Level 3</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>AMBULATION</strong></td>
                                    {
                                        ambulationData.map((item,index) => (
                                            <td key={index}>
                                                <div className='form-check form-check-inline'>
                                                    <input type='radio' className='form-check-input' id={`ambulation${index}`} {...register('ambulationLevel')} value={item.value} disabled={mutation.isPending}/>
                                                    <label htmlFor={`ambulation${index}`}>{item.title}</label>
                                                </div>
                                            </td>
                                        ))
                                    }
                                </tr>
                                <tr>
                                    <td><strong>TRANSFER</strong></td>
                                    {
                                        trasnferHygeineData.map((item,index) => (
                                            <td key={index}>
                                                <div className='form-check form-check-inline'>
                                                    <input type='radio' className='form-check-input' id={`transfer${index}`} {...register('transferLevel')} value={item.value} disabled={mutation.isPending}/>
                                                    <label htmlFor={`transfer${index}`}>{item.title}</label>
                                                </div>
                                            </td>
                                        ))
                                    }
                                </tr>
                                <tr>
                                    <td><strong>TOILETING</strong></td>
                                    {
                                        toiletingData.map((item,index) => (
                                            <td key={index}>
                                                <div className='form-check form-check-inline'>
                                                    <input type='radio' className='form-check-input' id={`toileting${index}`} {...register('toiletingLevel')} value={item.value} disabled={mutation.isPending}/>
                                                    <label htmlFor={`toileting${index}`}>{item.title}</label>
                                                </div>
                                            </td>
                                        ))
                                    }
                                </tr>
                                <tr>
                                    <td><strong>PERSONAL HYGIENE</strong></td>
                                    {
                                        trasnferHygeineData.map((item,index) => (
                                            <td key={index}>
                                                <div className='form-check form-check-inline'>
                                                    <input type='radio' className='form-check-input' id={`hygiene${index}`} {...register('hygieneLevel')} value={item.value} disabled={mutation.isPending}/>
                                                    <label htmlFor={`hygiene${index}`}>{item.title}</label>
                                                </div>
                                            </td>
                                        ))
                                    }
                                </tr>
                                <tr>
                                    <td><strong>COGNITION BEHAVIOR</strong></td>
                                    {
                                        cognitionData.map((item,index) => (
                                            <td key={index}>
                                                <div className='form-check form-check-inline'>
                                                    <input type='radio' className='form-check-input' id={`cognition${index}`} {...register('cognitionLevel')} value={item.value} disabled={mutation.isPending}/>
                                                    <label htmlFor={`cognition${index}`}>{item.title}</label>
                                                </div>
                                            </td>
                                        ))
                                    }
                                </tr>
                                <tr>
                                    <td><strong>MEDICAL/NURSING TREATMENT</strong></td>
                                    {
                                        medicalNursingData.map((item,index) => (
                                            <td key={index}>
                                                <div className='form-check form-check-inline'>
                                                    <input type='radio' className='form-check-input' id={`medical${index}`} {...register('medicalLevel')} value={item.value} disabled={mutation.isPending}/>
                                                    <label htmlFor={`medical${index}`}>{item.title}</label>
                                                </div>
                                            </td>
                                        ))
                                    }
                                </tr>
                                <tr>
                                    <td><strong>COMMUNICATION</strong></td>
                                    {
                                        communication.map((item,index) => (
                                            <td key={index}>
                                                <div className='form-check form-check-inline'>
                                                    <input type='radio' className='form-check-input' id={`communication${index}`} {...register('communicationLevel')} value={item.value} disabled={mutation.isPending}/>
                                                    <label htmlFor={`communication${index}`}>{item.title}</label>
                                                </div>
                                            </td>
                                        ))
                                    }
                                </tr>
                                <tr>
                                    <td><strong>Score</strong></td>
                                    <td className="mobileOnly"><strong>Level 1:</strong> {level1Total}</td>
                                    <td className="mobileOnly"><strong>Level 2:</strong> {level2Total}</td>
                                    <td className="mobileOnly"><strong>Level 3:</strong> {level3Total}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                    <div>
                        <strong>Total: </strong>
                        <span>{totalScore}</span>
                    </div>
                    <div>
                        <strong>Acuity Determination Level : </strong>
                        <span>{acuityDeterminationLevel}</span>
                    </div>
                    <div className='mt-1'>
                        <label htmlFor='comments'>Comments</label>
                        <textarea id='comments' {...register('comments')} rows={5} style={{width: '100%'}} disabled={mutation.isPending}/>
                    </div>
                    </Col>
                    <Col xs={12} className='mt-4'>
                        <div className='d-flex justify-content-end'>
                            <Button type='submit' title='Submit' aria-label='Submit' variant='primary' disabled={!isDirty || mutation.isPending}>
                                {
                                    mutation.isPending
                                    ?
                                        <Spinner animation="border" size="sm" />
                                    :
                                        <>Submit</>
                                }
                            </Button>
                        </div>        
                    </Col>
                </Row>
            </form>
        </div>
    )
};

export default ScreeningForm;
